import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FadeUp, Eyebrow } from "./motion";

const INDUSTRIES = [
  {
    id: "logistics",
    name: "Logistics & Warehousing",
    desc: "Order pickers and loaders perform thousands of bends per shift. ErgoAssist cuts fatigue and keeps throughput high.",
    img: "https://images.unsplash.com/photo-1586528116022-aeda1613c63d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHx3YXJlaG91c2UlMjBsb2dpc3RpY3MlMjB3b3JrZXJ8ZW58MHx8fHwxNzg0NTQyMTExfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "healthcare",
    name: "Healthcare & Caregiving",
    desc: "Nurses and caregivers lift and reposition patients daily. Reduce strain injuries and protect your most valuable staff.",
    img: "https://images.unsplash.com/photo-1666886573531-48d2e3c2b684?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxoZWFsdGhjYXJlJTIwcHJvZmVzc2lvbmFsJTIwdGVjaHxlbnwwfHx8fDE3ODQ1NDIxMTF8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "manufacturing",
    name: "Manufacturing & Assembly",
    desc: "Line workers holding awkward postures benefit from continuous passive support across full shifts.",
    img: "https://images.unsplash.com/photo-1524514587686-e2909d726e9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwbWVjaGFuaWNhbCUyMG1hY2hpbmVyeXxlbnwwfHx8fDE3ODQ1NDIxMTF8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "construction",
    name: "Construction & Field",
    desc: "Rugged, weatherable and battery-free — dependable assist for demanding, unpredictable site conditions.",
    img: "https://images.unsplash.com/photo-1711199694531-e982a79ea381?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHw0fHxpbmR1c3RyaWFsJTIwbWVjaGFuaWNhbCUyMG1hY2hpbmVyeXxlbnwwfHx8fDE3ODQ1NDIxMTF8MA&ixlib=rb-4.1.0&q=85",
  },
];

const UseCases = () => {
  const [active, setActive] = useState(0);

  return (
    <section id="industries" data-testid="industries-section" className="py-24 lg:py-36 bg-[#0a0a0a] border-t border-[#27272a]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <FadeUp>
          <Eyebrow num="02">Built for Industry</Eyebrow>
        </FadeUp>
        <FadeUp delay={0.05}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#f5f5f5] mt-6 max-w-3xl leading-[1.02]">
            Where physical work takes a toll, we take the load.
          </h2>
        </FadeUp>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 mt-16 items-start">
          {/* List */}
          <div className="flex flex-col">
            {INDUSTRIES.map((ind, i) => (
              <button
                key={ind.id}
                data-testid={`industry-item-${ind.id}`}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                className={`text-left border-t border-[#27272a] py-8 group transition-colors duration-300 ${
                  active === i ? "" : ""
                } last:border-b`}
              >
                <div className="flex items-baseline gap-5">
                  <span className="font-mono-accent text-xs text-[#ff4500]">0{i + 1}</span>
                  <div className="flex-1">
                    <h3
                      className={`font-display text-2xl lg:text-3xl tracking-tight transition-colors duration-300 ${
                        active === i ? "text-[#ff4500]" : "text-[#f5f5f5]"
                      }`}
                    >
                      {ind.name}
                    </h3>
                    <motion.p
                      initial={false}
                      animate={{ opacity: active === i ? 1 : 0.5 }}
                      className="text-sm lg:text-base text-[#a1a1aa] mt-3 max-w-md leading-relaxed"
                    >
                      {ind.desc}
                    </motion.p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Sticky image */}
          <div className="lg:sticky lg:top-28 relative aspect-[4/5] border border-[#27272a] overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.img
                key={INDUSTRIES[active].id}
                src={INDUSTRIES[active].img}
                alt={INDUSTRIES[active].name}
                initial={{ opacity: 0, scale: 1.06 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 w-full h-full object-cover grayscale"
                data-testid="industry-active-image"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/70 to-transparent pointer-events-none" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCases;
